import React, { useEffect, useState } from "react";

import HistoryIcon from "@mui/icons-material/History";
import PreviewIcon from "@mui/icons-material/TravelExplore";
import InfoIcon from "@mui/icons-material/Info";
import ReplayIcon from "@mui/icons-material/Replay";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import ResponsiveGrid from "../../grid/responsiveGrid";
import useMap from "../../hooks/useOpenLayersMap";
import MapFullScreen from "../../map/mapFullScreen";
import DfrPreview from "./dfrPreview";
import DfrPublish from "./dfrPublish";
import FlightHistoryDialog from "./flightHistoryDialog";
import FlightPlaybackDialog from "./flightPlaybackDialog";

import { Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { useUserAuth } from "../../contexts/authContext";
import { CustomPagination } from "../../customPagination";
import { ConvertCmpVolumesToFeatures, ConvertDfrToFeatures, GetSource, JumpToFeatures } from "../../map/mapUtil";
import { ConvertISOToDate } from "../../util";
import { FlightInfoDialog } from "./flightInfoDialog";

export default function OpManagerDashboard() {
    const [flights, setFlights] = useState([]);
    const [totalFlights, setTotalFlights] = useState(0);
    const [totalDfr, setTotalDfr] = useState(0);
    const [avgFlightsPerDay, setAvgFlightsPerDay] = useState("0");
    const [avgFlightsPerMonth, setAvgFlightsPerMonth] = useState("0");
    const [avgFlightDuration, setAvgFlightDuration] = useState("0m");
    const [totalFlightTime, setTotalFlightTime] = useState("0m");

    const [infoFlight, setInfoFlight] = useState(null);
    const [infoOpen, setInfoOpen] = useState(false);
    const [previewFlight, setPreviewFlight] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [historyFlight, setHistoryFlight] = useState(null);
    const [flightPlaybackDialogUuid, setFlightPlaybackDialogUuid] = useState(null);

    const [dfrFlights, setDfrFlights] = useState([]);
    const [dfrPublishFlight, setDfrPublishFlight] = useState(null);
    const [dfrPreviewFlight, setDfrPreviewFlight] = useState(null);
    const [dfrPublishOpen, setDfrPublishOpen] = useState(false);
    const [dfrPreviewOpen, setDfrPreviewOpen] = useState(false);

    const [sortModel, setSortModel] = useState([{ field: "time_end", sort: "desc" }]);

    const { user, handleFailedFetch } = useUserAuth();

    const columns = [
        {
            field: "info",
            headerName: "",
            minWidth: 50,
            maxWidth: 50,
            display: "flex",
            renderCell: (params) => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                    <IconButton size="medium" onClick={() => handleInfoClick(params.row)}>
                        <InfoIcon />
                    </IconButton>
                </Box>
            )
        },
        {
            field: "name",
            headerName: "Name",
            editable: false,
            minWidth: 150,
            flex: 1
        },
        {
            field: "time_start",
            headerName: "Start Date",
            editable: false,
            type: "dateTime",
            minWidth: 185,
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "time_end",
            headerName: "End Date",
            editable: false,
            type: "dateTime",
            minWidth: 185,
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "duration",
            headerName: "Duration",
            headerAlign: "center",
            align: "center",
            maxWidth: 100,
            flex: 1,
            editable: false,
            valueGetter: (value, row) => getFlightDuration(row),
            valueFormatter: (value) => formatDuration(value)
        },
        {
            field: "dfr",
            headerName: "DFR",
            headerAlign: "center",
            minWidth: 100,
            flex: 1,
            editable: false,
            renderCell: ({ row }) => {
                // Allow publish if the operation is in a final state (deleted or time expired).
                const hasEnded = new Date(row.time_end).getTime() <= new Date().getTime();
                const publishable = row.state === "DELETED" || hasEnded;
                if (!publishable) {
                    return <></>;
                }

                const dfrFlight = dfrFlights.find((dfr) => dfr.flight_uuid === row.flight_uuid);
                const published = dfrFlight && dfrFlight.published;
                const flight = {
                    flight_uuid: row.flight_uuid,
                    name: row.name,
                    incident: dfrFlight?.incident || "",
                    location: dfrFlight?.location || "",
                    summary: dfrFlight?.summary || "",
                    telemetry: dfrFlight?.telemetry,
                    volumes: row.volumes
                };

                return (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {published ? (
                            <Button
                                onClick={() => {
                                    setDfrPreviewFlight(flight);
                                    setDfrPreviewOpen(true);
                                }}
                            >
                                View
                            </Button>
                        ) : (
                            <Button
                                onClick={() => {
                                    setDfrPublishFlight(flight);
                                    setDfrPublishOpen(true);
                                }}
                            >
                                Publish
                            </Button>
                        )}
                    </Box>
                );
            }
        },
        {
            type: "actions",
            field: "actions",
            minWidth: 125,
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => [
                <Tooltip key={1} title="Preview">
                    <GridActionsCellItem
                        icon={<PreviewIcon />}
                        label="Preview"
                        onClick={() => {
                            setPreviewFlight(row);
                            setPreviewOpen(true);
                        }}
                        id={`preview-${row.volumeId}`}
                    />
                </Tooltip>,
                <Tooltip key={2} title="History">
                    <GridActionsCellItem icon={<HistoryIcon />} label="History" onClick={() => setHistoryFlight(row)} id={`history-${row.volumeId}`} />
                </Tooltip>,
                <Tooltip key={3} title="View Replay">
                    <GridActionsCellItem icon={<ReplayIcon />} label="View Replay" onClick={() => setFlightPlaybackDialogUuid(row.flight_uuid)} />
                </Tooltip>
            ]
        }
    ];

    const fetchOrgOperations = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`/api/op/getorg/${user.organization_id}?timestamp=${dayjs().subtract(6, "month").toISOString()}`, requestOptions)
            .then((res) => (res.ok ? res.json() : Promise.reject(res)))
            .then((data) => setFlights(data))
            .catch((err) => handleFailedFetch(err));
    };

    const fetchDFR = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/dfr/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setDfrFlights(data))
            .catch((err) => handleFailedFetch(err));
    };

    useEffect(() => {
        fetchOrgOperations();
        fetchDFR();
    }, []);

    useEffect(() => {
        setTotalFlights(flights.length);
        setTotalDfr(dfrFlights.filter((dfr) => dfr.published && flights.find((f) => f.flight_uuid === dfr.flight_uuid)).length);
        getAverageFlights(flights);
        getDurationFlights(flights);
    }, [flights, dfrFlights]);

    const getAverageFlights = (flights) => {
        if (flights.length === 0) {
            setAvgFlightsPerDay("0");
            setAvgFlightsPerMonth("0");
            return;
        }

        const formatAverage = (average) => {
            return Number.isInteger(average) ? `${average}` : average.toFixed(1);
        };

        const startTimes = flights.map((flight) => new Date(flight.time_start).getTime());
        const endTimes = flights.map((flight) => new Date(flight.time_end).getTime());
        const earliest = startTimes.length > 0 ? Math.min(...startTimes) : Date.now();
        const latest = endTimes.length > 0 ? Math.max(...endTimes) : Date.now();

        const days = Math.max(1, Math.ceil((latest - earliest) / (1000 * 60 * 60 * 24)));
        const months = days / 30;
        setAvgFlightsPerDay(formatAverage(flights.length / days));
        setAvgFlightsPerMonth(formatAverage(flights.length / months));
    };

    const getDurationFlights = (flights) => {
        const durations = flights.map((flight) => getFlightDuration(flight));
        const totalDuration = durations.reduce((acc, duration) => acc + duration, 0);
        setAvgFlightDuration(formatDuration(durations.length > 0 ? totalDuration / durations.length : 0));
        setTotalFlightTime(formatDuration(totalDuration));
    };

    const getFlightDuration = (flight) => {
        const startTime = new Date(flight.time_start).getTime();
        const endTime = new Date(flight.time_end).getTime();
        return endTime - startTime;
    };

    const formatDuration = (durationMs) => {
        const totalMinutes = Math.round(durationMs / (1000 * 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return hours > 0 ? `${hours}hrs ${minutes}m` : `${minutes}m`;
    };

    const handleInfoClick = (row) => {
        setInfoFlight(row);
        setInfoOpen(true);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, ml: "unset" }}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Paper
                        sx={{
                            p: 4,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: "space-between",
                            gap: 2,
                            height: 100
                        }}
                    >
                        <Typography variant="h5" gutterBottom>
                            Operations Manager Dashboard
                        </Typography>
                        <Typography variant="body1" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                            {`${dayjs().subtract(6, "month").format("MMM D, YYYY")} - ${dayjs().format("MMM D, YYYY")}`}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={12}>
                    <Paper
                        sx={{
                            p: 2,
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "space-around",
                            gap: 2
                        }}
                    >
                        {[
                            { title: "Flights", value: totalFlights, tooltip: "Number of flights that have been flown." },
                            { title: "DFR", value: totalDfr, tooltip: "Number of flights published to the DFR public site." },
                            {
                                title: "Avg Flights / Day",
                                value: avgFlightsPerDay,
                                tooltip: "Average number of flights per day."
                            },
                            {
                                title: "Avg Flights / Month",
                                value: avgFlightsPerMonth,
                                tooltip: "Average number of flights per month."
                            },
                            {
                                title: "Avg Flight Duration",
                                value: avgFlightDuration,
                                tooltip: "Average time elapsed from each flight."
                            },
                            {
                                title: "Total Flight Time",
                                value: totalFlightTime,
                                tooltip: "Total time elapsed from each flight."
                            }
                        ].map(({ title, value, tooltip }, i) => (
                            <Tooltip key={i} title={tooltip}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <Typography variant="body1" gutterBottom>
                                        {title}
                                    </Typography>
                                    <Typography variant="h6" gutterBottom sx={{ textAlign: "center", whiteSpace: "nowrap" }} id={title}>
                                        {value}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        ))}
                    </Paper>
                </Grid>
                <Grid size={12}>
                    <Paper sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                        <Box sx={{ height: 527, width: "100%" }}>
                            <ResponsiveGrid
                                rows={flights}
                                columns={columns}
                                disableVirtualization
                                disableRowSelectionOnClick
                                getRowId={(row) => row.flight_uuid}
                                pagination
                                slots={{ pagination: CustomPagination }}
                                initialState={{ pagination: { paginationModel: { pageSize: 8 } } }}
                                sortModel={sortModel}
                                onSortModelChange={(model) => setSortModel(model)}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {infoOpen && infoFlight && <FlightInfoDialog flight={infoFlight} flightInfoOpen={infoOpen} setFlightInfoOpen={setInfoOpen} />}
            {previewOpen && <MapPreview flight={previewFlight} setPreviewOpen={setPreviewOpen} />}
            {dfrPublishOpen && <DfrPublish flight={dfrPublishFlight} setDfrPublishOpen={setDfrPublishOpen} fetchDFR={fetchDFR} />}
            {dfrPreviewOpen && <DfrPreview dfr={dfrPreviewFlight} setDfrPreviewOpen={setDfrPreviewOpen} fetchDFR={fetchDFR} />}
            {historyFlight ? <FlightHistoryDialog selectedFlightHistoryFlight={historyFlight} setSelectedFlightHistoryFlight={setHistoryFlight} /> : <></>}

            {flightPlaybackDialogUuid ? (
                <FlightPlaybackDialog flightPlaybackDialogUuid={flightPlaybackDialogUuid} setFlightPlaybackDialogUuid={setFlightPlaybackDialogUuid} />
            ) : (
                <></>
            )}
        </Container>
    );
}

function MapPreview(props) {
    const MAP_ID = "map-preview";
    const map = useMap(MAP_ID, false);
    const [flight] = useState(props.flight);
    const [telemetry, setTelemetry] = useState([]);
    const { userMapSettings, handleFailedFetch } = useUserAuth();

    useEffect(() => {
        const fetchData = async () => {
            const requestOptions = {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            };
            await fetch(`/api/telemetry/get/${flight.flight_uuid}`, requestOptions)
                .then((response) => (response.ok ? response.json() : Promise.reject(response)))
                .then((data) => setTelemetry(data))
                .catch((err) => handleFailedFetch(err));
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (map) {
            map.addLayer(new VectorLayer({ source: new VectorSource(), properties: { name: "preview" } }));
        }
    }, [map]);

    useEffect(() => {
        const source = GetSource(map, "preview");
        if (!source) {
            return;
        }

        const features = ConvertCmpVolumesToFeatures([flight], userMapSettings);
        if (Array.isArray(telemetry) && telemetry.length > 0) {
            features.push(...ConvertDfrToFeatures({ ...flight, telemetry: true }, telemetry, userMapSettings));
        }

        source.clear();
        source.addFeatures(features);
        JumpToFeatures(map, features);
    }, [map, telemetry]);

    const handleClose = () => {
        props.setPreviewOpen(false);
    };

    return (
        <Dialog open={true} onClose={handleClose} fullWidth>
            <DialogTitle>Preview - {flight.name}</DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", width: "100%", height: "500px", pb: 0 }}>
                <Box sx={{ flex: 1 }}>
                    <MapFullScreen map_id={MAP_ID} map={map} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} sx={{ mr: "auto" }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
